import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ErrorState, Skeleton } from '@vta/ui';
import type { Team } from '@vta/types';
import { apiClient, ApiError } from '../lib/api-client.ts';
import { OgMeta } from '../lib/og-meta.tsx';
import { TeamHeader } from '../features/teams/TeamHeader.tsx';
import { TeamBio } from '../features/teams/TeamBio.tsx';
import { TeamEventsList } from '../features/teams/TeamEventsList.tsx';
import { RelatedTeams } from '../features/teams/RelatedTeams.tsx';
import { TeamCardDrawer } from '../features/teams/TeamCardDrawer.tsx';
import { useSelectedTeamStore } from '../stores/selected-team-store.ts';
import { formatTeamNumber } from '../lib/format.ts';

interface TeamResponse {
  team: Team;
}

/**
 * Dual-presentation team route.
 *
 * Mode selection — synchronous, no flicker:
 *   - If `selected-team-store.isOpen === true` AND its current `kind === 'team'`,
 *     the user arrived via the globe. Render the drawer overlay; the homepage
 *     route below is whatever the previous match was (the globe stays
 *     mounted because TanStack Router keeps parent routes mounted).
 *   - Otherwise (direct load, refresh, link from another tab) render the
 *     full-page layout.
 *
 * The drawer presentation happens on the homepage, where TeamCardDrawer is
 * mounted globally; here we *also* render TeamCardDrawer so a deep refresh
 * of `/team/839A` still gets a drawer-shaped UI when the store says drawer
 * mode (e.g. user opened the drawer, then deep-linked into the team page
 * within the same SPA session).
 */
export const Route = createFileRoute('/team/$teamNumber')({
  loader: async ({ params }) => {
    try {
      return await apiClient.get<TeamResponse>(`/api/teams/${encodeURIComponent(params.teamNumber)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return { team: null };
      }
      throw err;
    }
  },
  component: TeamRoute,
});

function TeamRoute() {
  const { teamNumber } = Route.useParams();
  const initialData = Route.useLoaderData() as TeamResponse | { team: null };
  const navigate = useNavigate();
  const selection = useSelectedTeamStore((s) => s.selection);
  const isDrawerOpen = useSelectedTeamStore((s) => s.isOpen);
  const closeDrawer = useSelectedTeamStore((s) => s.close);

  const drawerMode = isDrawerOpen && selection.kind === 'team' && selection.teamNumber === teamNumber;

  // Re-fetch via TanStack Query so we keep cache parity with the drawer.
  // initialData is reused so first paint never flickers.
  const query = useQuery<{ team: Team | null }>({
    queryKey: ['team', teamNumber],
    queryFn: ({ signal }) =>
      apiClient
        .get<TeamResponse>(`/api/teams/${encodeURIComponent(teamNumber)}`, { signal })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 404) return { team: null };
          throw err;
        }),
    initialData,
  });

  if (drawerMode) {
    // Drawer renders on top of whatever was previously routed. Closing the
    // drawer should drop us back there; if there's no prior history we go
    // home.
    return (
      <TeamCardDrawer
        teamNumber={teamNumber}
        onClose={() => {
          closeDrawer();
          navigate({ to: '/' });
        }}
      />
    );
  }

  if (query.isLoading) {
    return <FullPageSkeleton />;
  }

  if (query.isError) {
    return (
      <main className="container mx-auto py-10">
        <ErrorState
          title="Could not load this team"
          description="Refresh, or use search to find another team."
          retry={() => query.refetch()}
        />
      </main>
    );
  }

  const team = query.data?.team ?? null;

  if (!team) {
    return (
      <main className="container mx-auto py-10">
        <OgMeta
          title={`Team ${formatTeamNumber(teamNumber)} not found`}
          description="No team with that number is currently catalogued."
          pathname={`/team/${teamNumber}`}
        />
        <ErrorState
          title={`Team ${formatTeamNumber(teamNumber)} not found`}
          description={
            <span>
              The team number may be wrong, or it hasn&rsquo;t synced yet.{' '}
              <Link to="/explore" className="text-accent underline-offset-2 hover:underline">
                Browse all teams
              </Link>
              .
            </span>
          }
        />
      </main>
    );
  }

  const description = team.organization
    ? `${team.organization} — ${[team.city, team.country].filter(Boolean).join(', ') || 'VEX team'}.`
    : `${[team.city, team.country].filter(Boolean).join(', ') || 'VEX team'}.`;

  return (
    <>
      <OgMeta
        title={`${formatTeamNumber(team.teamNumber)} ${team.teamName}`}
        description={description}
        pathname={`/team/${team.teamNumber}`}
        type="profile"
      />
      <main className="container mx-auto flex flex-col gap-10 py-8 md:py-12">
        <TeamHeader team={team} />

        <div className="grid gap-10 lg:grid-cols-[2fr_1fr]">
          <div className="flex flex-col gap-10">
            <Section title="Events" subtitle="Registrations and divisions on file">
              <TeamEventsList teamNumber={team.teamNumber} />
            </Section>
            <Section title="Story" subtitle="User-submitted, admin-reviewed">
              <TeamBio bio={team.bio} />
            </Section>
          </div>

          <aside className="flex flex-col gap-10">
            <Section title="Related teams" subtitle={team.city ? `From ${team.city}` : team.country ?? ''}>
              <RelatedTeams
                teamNumber={team.teamNumber}
                citySlug={team.citySlug}
                countrySlug={team.countrySlug}
              />
            </Section>
          </aside>
        </div>
      </main>
    </>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between gap-3 border-b border-border-subtle pb-2">
        <h2 className="font-display text-xl text-fg">{title}</h2>
        {subtitle ? (
          <p className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">{subtitle}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function FullPageSkeleton() {
  return (
    <main className="container mx-auto flex flex-col gap-6 py-10">
      <Skeleton className="h-12 w-2/3" />
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="h-4 w-1/3" />
      <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    </main>
  );
}
